"use client";

import { useRouter, useSearchParams } from "next/navigation";
import React, { useRef, useState, useCallback } from "react";
import { InlineInfoBox } from "staysure-component-library";

import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import ConfirmationModal from "@/components/atoms/modal/confirmation-modal";
import ConfirmationsModal from "@/components/molecules/quote-section/edit-quote-section/confirmation-modal";
import withApplication from "@/components/with-application";
import withBrand from "@/components/with-brand";
import {
  AnnualMultiTrip,
  CoverEdit,
  Europe,
  QuestionThree,
  QuestionTwo,
  Worldwide,
} from "@/config/app-constant";
import useGtmPopup from "@/hooks/gtm/use-gtm-popup";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { LocationToContainerProps } from "@/types/LocationToType";
import selectLoationHookFlash from "@/utils/common.utils";
import {
  useFilteredTranslatedAnswersQTwoWhereGoing,
  useFindTranslatedCountryIdByValue,
} from "@/utils/question-location-to.utils";
import {
  handleScrollToRefAnyoneVisiting,
  smoothScrollToRef,
} from "@/utils/scrollUtils";

import EuropeAndWorldWideVisitSection from "./europe-and-world-wide-visit-section";
import LocationFromContainer from "./location-from/location-from-container";
import SingleTripLocation from "./single-trip-location";
import { useLocationToActions } from "../hooks/use-location-to-action";
import useLocationToData from "../hooks/use-location-to-data";
import { useLocationTo } from "../provider/location-to-provider";

function LocationToContainer(props: Readonly<LocationToContainerProps>) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { current } = useWizard();

  // State
  const [editModal, setEditModal] = useState(false);
  const [fromCountryCode, setFromCountryCode] = useState<string | undefined>(
    undefined
  );
  const [isDisable, setIsDisable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUKModal, setSelectedUKModal] = useState<number | null>(null);

  // Refs
  const locationToHeaderRef = useRef<HTMLHeadingElement>(null);
  const worldwideHeaderRef = useRef<HTMLHeadingElement>(null);
  const europeHeaderRef = useRef<HTMLHeadingElement>(null);
  const isStickyFooter = useStickyFooter();
  const actionButtonRef = useRef<HTMLDivElement>(null);

  // Props
  const {
    tripDetails,
    quoteLoading,
    selectedpolicyType,
    pageContent,
    toLocationCountryDetails,
    expatText,
    usagePage,
    setToLocationCountryDetails,
  } = props;
  const {
    modalTitleText,
    modalSubText,
    modalConfirmBtnText,
    modalCancelBtnText,
    sameCountryHeaderText,
  } = pageContent;
  const locationFromContent = pageContent?.locationFrom?.["0"];
  const { toggleWrapperRef } = useLocationTo();

  // Hooks
  const { resetQuestionData } = useResetStateTravelerEdit();
  const { isSubmitBtnClicked, payload } = useLocationTo();
  const {
    questionState,
    setQuestionState,
    modifyPayloadToLocation,
    selectedAMTEuropeWorldwide,
    setSelectedAMTEuropeWorldwide,
  } = useLocationToData({
    tripDetails,
    selectedpolicyType,
    toLocationCountryDetails,
  });

  const coverEdit = searchParams.get(CoverEdit);
  const { toCountrySelected, valueSelected, countryArray } = questionState;
  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails ?? undefined
  );

  // Translation hooks
  const filteredTranslatedAnswers = useFilteredTranslatedAnswersQTwoWhereGoing(
    current.brandSpecificParams?.locationToCountries ?? []
  );
  const findTranslatedCountryId = useFindTranslatedCountryIdByValue(
    toCountrySelected ?? ""
  );

  // Use custom hook for click actions
  const {
    selectedEurope,
    selectedWorldWide,
    selectedCountry,
    modifyModalButtonClick,
    handleFromCountrySelection,
  } = useLocationToActions({
    setQuestionState,
    setSelectedAMTEuropeWorldwide,
    setSelectedUKModal,
    setModalOpen,
    setFromCountryCode,
    modifyPayloadToLocation, // Replace with your actual function
    fromCountryCode,
    questionState,
    toCountrySelected,

    selectedpolicyType,
    toLocationCountryDetails,
    actionButtonRef,
    worldwideHeaderRef,
    europeHeaderRef,
    isStickyFooter,
  });

  // Handlers
  const getPopupName = useCallback(
    () =>
      modalOpen
        ? "UK Destination Confirm"
        : sameCountryHeaderText ?? "Information",
    [modalOpen, sameCountryHeaderText]
  );

  useGtmPopup({
    tripType: QuestionThree,
    isPopupOpen: modalOpen,
    popupName: getPopupName(),
  });

  useGtmQuestionSet({
    pageTitle: QuestionThree,
    tripType: selectedpolicyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise
      ? payload?.attributes?.tripDetails?.goingOnCruise
      : undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation ?? "",
    travelGroupType: undefined,
    isSubmitBtnClicked,
  });

  // Renderers
  const renderQuestionTwoSectionAMT = () => {
    const { amtQuestionOneText } = pageContent;
    const countryId =
      !toCountrySelected || !selectedAMTEuropeWorldwide
        ? selectedUKModal
        : findTranslatedCountryId?.id;
    return (
      <div data-testid="multi-trip-support-content">
        {current.uiElementControls?.locationFrom && (
          <LocationFromContainer
            questionOneText={locationFromContent?.questionOneText}
            questionOnePlaceHolder={locationFromContent?.questionOnePlaceHolder}
            setFromCountry={handleFromCountrySelection}
            locationFromCountreyClick={() => {
              const element1 = document.getElementById("paragaph-test-id");
              if (element1)
                handleScrollToRefAnyoneVisiting(
                  actionButtonRef,
                  !isStickyFooter
                );
              else smoothScrollToRef(locationToHeaderRef);
            }}
          />
        )}
        <h1
          className="font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
          ref={locationToHeaderRef}
        >
          {amtQuestionOneText}
        </h1>
        <ButtonGroup
          setSelectedAnswer={selectedCountry}
          selectedAnswer={countryId}
          answerArray={filteredTranslatedAnswers}
          gridCols={
            current.uiElementControls?.locationToButtonInTwoLineForDesktop
              ? 2
              : undefined
          }
          location={QuestionTwo}
          type="to-location"
          isDisable={isDisable}
          onOptionClick={(id) => {
            if (current.uiElementControls?.locationFrom && id === 3)
              setTimeout(
                () =>
                  handleScrollToRefAnyoneVisiting(
                    actionButtonRef,
                    !isStickyFooter
                  ),
                400
              );
            if (
              !current.uiElementControls?.locationFrom &&
              (id === 2 || id === 3)
            )
              setTimeout(() => {
                let targetRef;
                if (isStickyFooter) {
                  targetRef = actionButtonRef;
                } else if (id === 3) {
                  targetRef = worldwideHeaderRef;
                } else {
                  targetRef = europeHeaderRef;
                }
                handleScrollToRefAnyoneVisiting(targetRef, !isStickyFooter);
              }, 500);
          }}
        />
        <ConfirmationModal
          modalTitleText={modalTitleText}
          modalSubText={modalSubText}
          modalConfirmBtnText={modalConfirmBtnText}
          modalCancelBtnText={modalCancelBtnText}
          isOpen={modalOpen}
          modalConfirmBtnClick={modifyModalButtonClick}
        />
      </div>
    );
  };

  const renderQuestionTwoSection = () => (
    <div className="w-full">
      {expatText && (
        <div className="mt-spacing-m md:mb-spacing-xxl mb-0">
          <InlineInfoBox
            id="location-to-expat-info-box"
            content={<p>{expatText}</p>}
            style={{
              width: "100%",
              backgroundColor: "#EDF2FF",
              borderColor: "#006BD6",
              padding: "8px 16px",
              borderRadius: "8px",
              color: "#272427",
              fontSize: "16px",
              lineHeight: "24px",
            }}
          />
        </div>
      )}
      {selectedpolicyType !== AnnualMultiTrip && (
        <SingleTripLocation
          quoteLoading={quoteLoading}
          fromCountryCode={fromCountryCode}
          pageContent={pageContent}
          actionButtonRef={actionButtonRef}
          setFromCountry={handleFromCountrySelection}
          modifyPayloadToLocation={modifyPayloadToLocation}
          setToLocationCountryDetails={setToLocationCountryDetails}
          setIsDisable={setIsDisable}
          countryArray={countryArray}
        />
      )}
      {selectedpolicyType === AnnualMultiTrip && renderQuestionTwoSectionAMT()}
      {(selectedAMTEuropeWorldwide === Europe ||
        selectedAMTEuropeWorldwide === Worldwide) && (
        <EuropeAndWorldWideVisitSection
          selectedAnswer={
            selectedAMTEuropeWorldwide === Europe
              ? selectedEurope
              : selectedWorldWide
          }
          valueSelected={valueSelected}
          isDisable={isDisable}
          quoteLoading={quoteLoading}
          amtQuestionTwoText={pageContent?.amtQuestionTwoText}
          amtQuestionTwoDescription={
            selectedAMTEuropeWorldwide === Europe
              ? pageContent?.amtQuestionTwoEuropeDiscrip
              : pageContent?.amtQuestionTwoWorldWideDiscrip
          }
          usagePage={usagePage}
          headerRef={
            selectedAMTEuropeWorldwide === Europe
              ? europeHeaderRef
              : worldwideHeaderRef
          }
          actionButtonRef={actionButtonRef}
          type={
            selectedAMTEuropeWorldwide === Europe
              ? "europe-location"
              : "worldwide-location"
          }
        />
      )}
    </div>
  );

  return (
    <div data-testid="question-two-test-id">
      <div
        className={`lg:max-w-[414px] md:max-w-[400px] max-w-[unset] w-full mx-auto relative h-full flex flex-col justify-between ${
          current.uiElementControls?.disableBrandStyling
            ? ""
            : "md:min-h-[225px]"
        }`}
        ref={toggleWrapperRef}
      >
        {renderQuestionTwoSection()}
      </div>
      <ConfirmationsModal
        isOpen={editModal}
        handleConfirmBtnClick={() => {
          router.push(current.edges.cancelEdit ?? "");
          // router.push(`/${Quote}/?${QuoteEdit}=true`);
          if (coverEdit) resetQuestionData();
        }}
        handleCancelmBtnClick={() => setEditModal(false)}
      />
    </div>
  );
}

export default withBrand(withApplication(LocationToContainer));
